import React, { useState } from 'react';
import styled from 'styled-components';
import * as R from 'ramda';
import { useIntl } from 'react-intl';
import { Product } from '@inter-ikea-storageone/range';
import { KompisPill } from '@inter-ikea-kompis/react-components';
import { NewFilterIcon } from 'components/ProductSidebar/FilterButton/FilterNewSVG';
import PillSizeOptionsEnum from '@inter-ikea-kompis/component-pill/lib/enums/PillSizeOptionsEnum';
import FocusOutlinePositionWatcherEnum from '@inter-ikea-kompis/component-focus-outline/lib/enums/FocusOutlinePositionWatcherEnum';
import SelectedMenuFilters from 'components/ProductSidebar/menus/SelectedMenuFilters';
import { Filter } from 'rangeConfig/@types';
import { SkapaTheme } from '@inter-ikea-kompis/themes';

const Wrapper = styled.div`
  margin-top: ${({ theme }) => theme.space.md};
`;

type Filters = {
  productIdList: string[];
  filterState: FilterState;
};

type FilterState = {
  [key: string]: boolean;
};

type SelectedProductFilters = {
  [id: string]: Filters;
};

type Props = {
  filters: Filter[];
  products: Product[];
  onProductListFilters: (products: Product[]) => void;
};

const ProductListFilters = ({ filters, products, onProductListFilters }: Props) => {
  const { formatMessage } = useIntl();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [selectedProductFilters, setSelectedProductFilters] = useState<SelectedProductFilters>({});
  const productIds = products.map(p => p.coreProductId);

  const noFilterToggleButton = filters.find(filter => filter.noToggleButton === true);

  const filterProducts = (id: string, productIdList: string[], filterState: FilterState) => {
    const newSelectedProductFilters = {
      ...selectedProductFilters,
      [id]: { productIdList, filterState }
    };

    const filteredProductIds = Object.values(newSelectedProductFilters).reduce(
      (acc: string[], f: Filters) => {
        return R.intersection(acc, f.productIdList);
      },
      productIds
    );

    const filteredProducts = products.filter(product =>
      filteredProductIds.includes(product.coreProductId)
    );

    onProductListFilters(filteredProducts);
    setSelectedProductFilters(newSelectedProductFilters);
  };

  return (
    <Wrapper>
      {noFilterToggleButton ? (
        <SelectedMenuFilters
          availableFilters={filters}
          products={products}
          filterProducts={filterProducts}
          isFilterVisible={true}
          selectedProductFilters={selectedProductFilters}
          noAnimationWrapper={true}
        />
      ) : (
        <>
          <KompisPill
            label={formatMessage({ id: 'storageone.filterButtonNewMenu' })}
            icon={NewFilterIcon}
            theme={SkapaTheme}
            onSelect={() => setIsFilterVisible(!isFilterVisible)}
            pillSize={PillSizeOptionsEnum.small}
            aria-selected={isFilterVisible}
            aria-expanded={isFilterVisible}
            focusOutlinePositionWatcher={FocusOutlinePositionWatcherEnum.disabled}
          />
          <SelectedMenuFilters
            availableFilters={filters}
            products={products}
            filterProducts={filterProducts}
            isFilterVisible={isFilterVisible}
            selectedProductFilters={selectedProductFilters}
          />
        </>
      )}
    </Wrapper>
  );
};

export default ProductListFilters;
